import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { expensesApi } from 'src/Services/API';
import { ExpenseWithSharesDto, UpdateExpenseDto } from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';

type UpdateExpenseParams = {
  expenseId: string;
  updateData: UpdateExpenseDto;
};

export const useUpdateExpense = (
  options?: UseMutationOptions<ExpenseWithSharesDto, unknown, UpdateExpenseParams>
) => {
  const queryClient = useQueryClient();
  const assetId = useCurrentAssetId();

  return useMutation<ExpenseWithSharesDto, unknown, UpdateExpenseParams>(
    ['update-expense', assetId],
    async ({ expenseId, updateData }: UpdateExpenseParams) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }

      const response = await expensesApi.expensesControllerUpdate(expenseId, assetId, updateData);
      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries(['fetch-expenses', assetId]);
        queryClient.invalidateQueries(['fetch-expense', variables.expenseId]);
        queryClient.invalidateQueries('fetch-expense-balance');
        queryClient.invalidateQueries('fetch-expense-stats');
        queryClient.invalidateQueries({ queryKey: ['fetch-personal-wallet'] });
        options?.onSuccess?.(data, variables, context);
        trackEvent('Expense', 'update', data.title);
      },
    }
  );
};
